/**
 * Coordinates filter-menu popover dismiss guards: nested portaled controls, checkbox/radio
 * focus handoffs, explicit trigger-close intent, and scroll-dismiss suppression.
 *
 * TODO(cui-drift): Generic pieces here (protected-target detection, scroll-close
 * mirroring) may belong in `@calendly/ui/components/filter-menu` once CUI owns this
 * behavior end-to-end.
 */
import { useEffect, useRef } from 'react'

import {
  getFilterMenuContainerElement,
  getFilterMenuTriggerElement,
} from 'hooks/filter_menu_container_registry'
import { getShellContentScrollElement } from 'shared/shell_content_scroll_registry'

import { getFilterMenuContentElement } from './filter_menu_content_registry'
import { SCHEDULING_FILTER_MENU_PORTAL_ID } from './filter_menu_portal_root'
import { getFilterMenuPortalRoots } from './filter_menu_portals'

const MEETINGS_FILTER_MENU_PORTAL_ID = 'meetings-filter-menu-portal'

const FILTER_MENU_CONTENT_TEST_IDS = [
  'scheduling-filter-menu-content',
  'meetings-filter-menu-content',
]

const FILTER_MENU_PANEL_ROOT_SELECTOR = FILTER_MENU_CONTENT_TEST_IDS
  .map((id) => `[data-testid="${id}"]`)
  .join(', ')

const PANEL_INTERACTION_MS = 500

let suppressCloseForPanelCheckboxUntil = 0
let filterMenuTriggerCloseRequested = false
let filterMenuTriggerPressedAt = 0
let filterMenuBlockCheckboxRefocusUntil = 0
let filterMenuSuppressTriggerToggleOpenUntil = 0

const FILTER_MENU_TRIGGER_PRESS_MS = 500
const FILTER_MENU_BLOCK_CHECKBOX_REFOCUS_MS = 300
const FILTER_MENU_SUPPRESS_TRIGGER_TOGGLE_MS = 100

/** Saved scrollTop values while a panel control owns focus (WeakMap keyed by scroll container). */
const scrollPositionsDuringPanelInteraction = new WeakMap()

/** Dismiss guards for currently open filter menus (supports multiple mounted instances). */
const activeDismissGuardRefs = new Set()

/** Scroll behavior for currently open filter menus. */
const activeFilterMenuScrollBehaviors = new Set()

const isFilterMenuOpenForDismissGuard = () => (
  activeDismissGuardRefs.size > 0 || Boolean(getFilterMenuContentElement())
)

/** --- Panel region detection (checkbox/radio focus handoff) --- */

const noteFilterMenuTriggerPress = () => {
  filterMenuTriggerPressedAt = Date.now()
}

/** True while a trigger press is closing the menu (must not be blocked by dismiss guards). */
export const isFilterMenuTriggerClosePending = () => (
  filterMenuTriggerCloseRequested
  || Date.now() - filterMenuTriggerPressedAt <= FILTER_MENU_TRIGGER_PRESS_MS
)

const isFilterMenuPanelNode = (node) => (
  node instanceof Element && Boolean(node.closest(FILTER_MENU_PANEL_ROOT_SELECTOR))
)

const isNodeInsideFilterMenuPanel = (node) => {
  if (!(node instanceof Element)) return false

  const menuContent = getFilterMenuContentElement()
  if (menuContent?.contains(node)) return true

  return isFilterMenuPanelNode(node)
}

/** CUI CheckboxInput/RadioInput visual control immediately after a hidden native input. */
const isCuiCheckboxOrRadioVisualTarget = (node) => {
  if (!(node instanceof Element) || !isNodeInsideFilterMenuPanel(node)) return false

  const previous = node.previousElementSibling
  return previous instanceof HTMLInputElement
    && (previous.type === 'checkbox' || previous.type === 'radio')
}

/** Native checkbox/radio input or label wrapping one, inside the open filter menu panel. */
const isNodeInsideFilterMenuPanelCheckbox = (node) => {
  if (!(node instanceof Element) || !isNodeInsideFilterMenuPanel(node)) return false

  if (node instanceof HTMLInputElement && (node.type === 'checkbox' || node.type === 'radio')) {
    return true
  }

  const label = node.closest('label')
  return Boolean(label?.querySelector('input[type="checkbox"], input[type="radio"]'))
}

/** Checkbox or radio control inside the panel (native input, label, or CUI visual sibling). */
const isFilterMenuPanelCheckboxInteractionTarget = (node) => (
  isNodeInsideFilterMenuPanelCheckbox(node) || isCuiCheckboxOrRadioVisualTarget(node)
)

/** Focus moves to a panel checkbox/radio during CUI's hidden-input focus handoff. */
const isFilterMenuPanelFocusHandoffTarget = (node) => (
  isFilterMenuPanelCheckboxInteractionTarget(node)
)

const isFilterMenuPanelCheckboxPointerEvent = (event) => {
  if (!(event instanceof Event)) return false

  return event.composedPath().some((node) => isFilterMenuPanelCheckboxInteractionTarget(node))
}

const getFilterMenuTriggerNode = () => (
  getFilterMenuTriggerElement()
    ?? getFilterMenuContainerElement()?.querySelector('[aria-haspopup="dialog"]')
)

const isFilterMenuTriggerNode = (node) => {
  const trigger = getFilterMenuTriggerNode()
  if (!trigger || !(node instanceof Node)) return false

  return trigger === node || trigger.contains(node)
}

const isFilterMenuTriggerPointerEvent = (event) => {
  if (!(event instanceof Event)) return false

  if (event.composedPath().some(isFilterMenuTriggerNode)) return true

  const target = event.target
  if (!(target instanceof Element)) return false

  const triggerButton = target.closest('[aria-haspopup="dialog"]')
  if (!triggerButton) return false

  const container = getFilterMenuContainerElement()
  const menuContent = getFilterMenuContentElement()

  return Boolean(container?.contains(triggerButton) && !menuContent?.contains(triggerButton))
}

/** --- Trigger-close intent (re-click trigger while panel is open) --- */

const markFilterMenuTriggerCloseIntent = () => {
  suppressCloseForPanelCheckboxUntil = 0
  filterMenuTriggerCloseRequested = true
  filterMenuBlockCheckboxRefocusUntil = Date.now() + FILTER_MENU_BLOCK_CHECKBOX_REFOCUS_MS
  activeDismissGuardRefs.forEach((ignoreDismissRef) => {
    ignoreDismissRef.current = false
  })
}

const resetFilterMenuTriggerCloseState = () => {
  filterMenuTriggerCloseRequested = false
  filterMenuTriggerPressedAt = 0
  filterMenuBlockCheckboxRefocusUntil = 0
  filterMenuSuppressTriggerToggleOpenUntil = 0
}

/** Prevents the trigger from immediately re-opening after closing from a focused checkbox. */
export const shouldSuppressFilterMenuTriggerToggleOpen = () => (
  Date.now() <= filterMenuSuppressTriggerToggleOpenUntil
)

/** --- Page scroll stabilization during checkbox/radio focus --- */

const isScrollablePageContainer = (element) => {
  if (!(element instanceof HTMLElement)) return false

  const { overflowY } = window.getComputedStyle(element)

  return /auto|scroll|overlay/.test(overflowY) && element.scrollHeight > element.clientHeight
}

const getFilterMenuPageScrollContainers = () => {
  const containers = []
  const seen = new Set()

  let node = getFilterMenuTriggerNode()?.parentElement

  while (node) {
    if (!seen.has(node) && isScrollablePageContainer(node)) {
      seen.add(node)
      containers.push(node)
    }

    node = node.parentElement
  }

  const shellContentScroll = getShellContentScrollElement()
  if (shellContentScroll instanceof HTMLElement && !seen.has(shellContentScroll)) {
    containers.push(shellContentScroll)
  }

  return containers
}

const rememberFilterMenuPageScrollPositions = () => {
  getFilterMenuPageScrollContainers().forEach((container) => {
    scrollPositionsDuringPanelInteraction.set(container, container.scrollTop)
  })
}

const markFilterMenuPanelCheckboxInteraction = () => {
  suppressCloseForPanelCheckboxUntil = Date.now() + PANEL_INTERACTION_MS
}

const isWithinPanelCheckboxInteractionWindow = () => (
  Date.now() <= suppressCloseForPanelCheckboxUntil
)

const revertFilterMenuPageScrollIfNeeded = (scrollTarget) => {
  if (!isWithinPanelCheckboxInteractionWindow()) return false
  if (!(scrollTarget instanceof HTMLElement)) return false

  const savedScrollTop = scrollPositionsDuringPanelInteraction.get(scrollTarget)
  if (savedScrollTop === undefined) return false

  if (scrollTarget.scrollTop !== savedScrollTop) {
    scrollTarget.scrollTop = savedScrollTop
    return true
  }

  return false
}

const stabilizeCheckboxFocusWithoutPageScroll = (target) => {
  if (!(target instanceof HTMLElement)) return

  target.focus({ preventScroll: true })
}

const blurFilterMenuPanelFocus = () => {
  const menuContent = getFilterMenuContentElement()
  const active = document.activeElement

  if (!(active instanceof HTMLElement) || !menuContent?.contains(active)) return

  active.blur()
}

const allowFilterMenuCloseWhenCheckboxFocused = () => {
  if (!isFilterMenuPanelFocusHandoffTarget(document.activeElement)) return false

  blurFilterMenuPanelFocus()
  markFilterMenuTriggerCloseIntent()
  return true
}

const restoreRememberedFilterMenuPageScrollPositions = () => {
  getFilterMenuPageScrollContainers().forEach((container) => {
    const savedScrollTop = scrollPositionsDuringPanelInteraction.get(container)
    if (savedScrollTop === undefined) return

    container.scrollTop = savedScrollTop
  })
}

const restorePageScrollAfterCheckboxFocus = () => {
  requestAnimationFrame(() => {
    restoreRememberedFilterMenuPageScrollPositions()
  })
}

const prepareFilterMenuTriggerClose = () => {
  const checkboxHadFocus = isFilterMenuPanelFocusHandoffTarget(document.activeElement)

  rememberFilterMenuPageScrollPositions()
  markFilterMenuTriggerCloseIntent()
  blurFilterMenuPanelFocus()

  if (checkboxHadFocus) {
    restoreRememberedFilterMenuPageScrollPositions()
    filterMenuSuppressTriggerToggleOpenUntil = Date.now() + FILTER_MENU_SUPPRESS_TRIGGER_TOGGLE_MS
  }
}

const handleFilterMenuTriggerClosePointerDown = (event) => {
  if (!isFilterMenuTriggerPointerEvent(event)) return

  noteFilterMenuTriggerPress()

  if (!isFilterMenuOpenForDismissGuard()) return

  prepareFilterMenuTriggerClose()
}

const handleFilterMenuTriggerCloseFocusIn = (event) => {
  if (Date.now() > filterMenuBlockCheckboxRefocusUntil) return
  if (!isFilterMenuPanelFocusHandoffTarget(event.target)) return

  event.target.blur()
}

/** Whether a panel checkbox or radio currently owns focus. */
export const isFilterMenuPanelCheckboxFocused = () => (
  isFilterMenuPanelFocusHandoffTarget(document.activeElement)
)

export {
  /** Blurs panel focus and arms trigger-close before React Aria refocuses the trigger. */
  prepareFilterMenuTriggerClose,
  /** Blurs a focused panel checkbox/radio so the menu can close on outside click. */
  allowFilterMenuCloseWhenCheckboxFocused,
}

/** Resets module-level close-suppression state after the popover finishes closing. */
export const finalizeFilterMenuCloseSuppression = () => {
  resetFilterMenuTriggerCloseState()
  suppressCloseForPanelCheckboxUntil = 0
  activeDismissGuardRefs.forEach((ignoreDismissRef) => {
    ignoreDismissRef.current = false
  })
}

const armFilterMenuDismissGuard = () => {
  activeDismissGuardRefs.forEach((ignoreDismissRef) => {
    ignoreDismissRef.current = true
  })
}

const armTemporaryFilterMenuDismissGuard = () => {
  if (filterMenuTriggerCloseRequested) return

  markFilterMenuPanelCheckboxInteraction()
  armFilterMenuDismissGuard()
  window.setTimeout(() => {
    if (filterMenuTriggerCloseRequested) return

    activeDismissGuardRefs.forEach((ignoreDismissRef) => {
      ignoreDismissRef.current = false
    })
  }, PANEL_INTERACTION_MS)
}

/** CUI moves focus to a visually hidden native input after clicking the visual control. */
const isHiddenCheckboxFocusTarget = (node) => {
  if (!(node instanceof HTMLInputElement)) return false
  if (node.type !== 'checkbox' && node.type !== 'radio') return false

  // CUI CheckboxInput focuses a VisuallyHidden native input after clicking the visual control.
  return isWithinPanelCheckboxInteractionWindow() && Boolean(getFilterMenuContentElement())
}

const PROTECTED_CLOSEST_SELECTORS = [
  `#${SCHEDULING_FILTER_MENU_PORTAL_ID}`,
  `#${MEETINGS_FILTER_MENU_PORTAL_ID}`,
  ...FILTER_MENU_CONTENT_TEST_IDS.map((id) => `[data-testid="${id}"]`),
  '[data-component="filter-dropdown-menu"]',
  '[data-testid="filter-dropdown-trigger"]',
  '[data-testid="filter-date-range-trigger"]',
  '[data-testid="packages-filter-trigger"]',
].join(', ')

const DISMISS_GUARD_CLOSEST_SELECTORS = [
  `#${SCHEDULING_FILTER_MENU_PORTAL_ID}`,
  `#${MEETINGS_FILTER_MENU_PORTAL_ID}`,
  '[data-component="filter-dropdown-menu"]',
  '[data-testid="filter-dropdown-trigger"]',
  '[data-testid="filter-date-range-trigger"]',
  '[data-testid="packages-filter-trigger"]',
].join(', ')

/** --- Protected / dismiss-guard target matching --- */

const nodeContainedByPortalRoots = (node) => getFilterMenuPortalRoots().some(
  (portal) => portal?.contains(node),
)

const nodeMatchesProtectedRegion = (node) => {
  if (!(node instanceof Node)) return false

  if (nodeContainedByPortalRoots(node)) return true

  const menuContent = getFilterMenuContentElement()
  if (menuContent?.contains(node)) return true

  if (node instanceof Element && node.closest(PROTECTED_CLOSEST_SELECTORS)) {
    return true
  }

  if (isHiddenCheckboxFocusTarget(node)) {
    return true
  }

  if (isFilterMenuPanelCheckboxInteractionTarget(node)) {
    return true
  }

  return false
}

const nodeMatchesDismissGuardRegion = (node) => {
  if (!(node instanceof Node)) return false

  if (nodeContainedByPortalRoots(node)) return true

  if (node instanceof Element && node.closest(DISMISS_GUARD_CLOSEST_SELECTORS)) {
    return true
  }

  if (isFilterMenuPanelCheckboxInteractionTarget(node)) {
    return true
  }

  return false
}

/** Whether an event target lies in a region that must not dismiss the filter menu. */
export const isFilterMenuProtectedTarget = (target) => {
  if (target == null) return false

  const nodes = target instanceof Event ? target.composedPath() : [target]

  return nodes.some(nodeMatchesProtectedRegion)
}

/** Whether a pointer target should arm the temporary dismiss guard (portals, nested triggers). */
export const isFilterMenuDismissGuardTarget = (target) => {
  if (target == null) return false

  const nodes = target instanceof Event ? target.composedPath() : [target]

  return nodes.some(nodeMatchesDismissGuardRegion)
}

/** Whether React Aria interact-outside close should be suppressed for the open panel. */
export const shouldSuppressFilterMenuClose = () => {
  if (isFilterMenuTriggerClosePending()) return false

  if (isWithinPanelCheckboxInteractionWindow()) return true

  for (const ignoreDismissRef of activeDismissGuardRefs) {
    if (ignoreDismissRef.current) return true
  }

  return false
}

/**
 * Mirrors React Aria `useCloseOnScroll` targeting, but only for scroll containers that would
 * dismiss a non-modal popover (ancestors of the trigger, excluding the panel's inner scroll).
 */
export const isFilterMenuScrollCloseEvent = (event) => {
  if (!(event instanceof Event) || event.type !== 'scroll') return false
  if (activeDismissGuardRefs.size === 0) return false

  const target = event.target
  const trigger = getFilterMenuTriggerElement()
    ?? getFilterMenuContainerElement()?.querySelector('[aria-haspopup="dialog"]')
  if (!trigger) return false

  if (!(target instanceof Node)) {
    return true
  }

  if (!target.contains(trigger)) {
    return false
  }

  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return false
  }

  const menuContent = getFilterMenuContentElement()
  if (menuContent instanceof Element && target instanceof Element && menuContent.contains(target)) {
    return false
  }

  return true
}

/** --- Global scroll-close suppression (module init) --- */

const shouldSuppressFilterMenuScrollClose = () => {
  if (activeFilterMenuScrollBehaviors.size === 0) return true

  for (const behavior of activeFilterMenuScrollBehaviors) {
    if (!behavior.closeOnPageScroll) {
      return true
    }
  }

  return false
}

const markFilterMenuScrollCloseSuppressed = () => {
  // Scroll is blocked via stopImmediatePropagation; no dismiss guard needed here.
}

let scrollCloseSuppressionListenerInstalled = false

const installFilterMenuScrollCloseSuppressionListener = () => {
  if (scrollCloseSuppressionListenerInstalled) return

  scrollCloseSuppressionListenerInstalled = true

  // Register before popover mount so this runs ahead of React Aria's capture listener.
  window.addEventListener('scroll', (event) => {
    revertFilterMenuPageScrollIfNeeded(event.target)

    if (!isFilterMenuScrollCloseEvent(event)) return
    if (!shouldSuppressFilterMenuScrollClose()) return

    markFilterMenuScrollCloseSuppressed()
    event.stopImmediatePropagation()
  }, true)
}

installFilterMenuScrollCloseSuppressionListener()

let filterMenuTriggerCloseListenerInstalled = false

const installFilterMenuTriggerCloseListener = () => {
  if (filterMenuTriggerCloseListenerInstalled) return

  filterMenuTriggerCloseListenerInstalled = true

  // Run before React Aria refocuses the last dialog control on trigger mousedown.
  window.addEventListener('pointerdown', handleFilterMenuTriggerClosePointerDown, true)
  window.addEventListener('mousedown', handleFilterMenuTriggerClosePointerDown, true)
  window.addEventListener('focusin', handleFilterMenuTriggerCloseFocusIn, true)
}

installFilterMenuTriggerCloseListener()

/**
 * Clears stale dismiss-guard state after programmatic nested-dropdown closes (e.g. Escape).
 * Scroll behavior is owned by {@link useFilterMenuPortalDismissGuard} for the open panel.
 */
export const clearFilterMenuPortalDismissGuard = () => {
  suppressCloseForPanelCheckboxUntil = 0
  resetFilterMenuTriggerCloseState()
  activeDismissGuardRefs.forEach((ignoreDismissRef) => {
    ignoreDismissRef.current = false
  })
}

/**
 * Tracks pointer/focus interactions that should not dismiss the filter menu popover.
 *
 * Registers this instance in module-level sets so global capture listeners can consult
 * {@link shouldSuppressFilterMenuClose} and {@link isFilterMenuScrollCloseEvent}.
 */
export const useFilterMenuPortalDismissGuard = (
  isFilterMenuOpen,
  { closeOnPageScroll = false } = {},
) => {
  const ignoreDismissRef = useRef(false)

  useEffect(() => {
    if (!isFilterMenuOpen) return undefined

    resetFilterMenuTriggerCloseState()

    activeDismissGuardRefs.add(ignoreDismissRef)
    const scrollBehavior = { closeOnPageScroll }
    activeFilterMenuScrollBehaviors.add(scrollBehavior)

    return () => {
      activeDismissGuardRefs.delete(ignoreDismissRef)
      activeFilterMenuScrollBehaviors.delete(scrollBehavior)
    }
  }, [closeOnPageScroll, isFilterMenuOpen])

  useEffect(() => {
    if (!isFilterMenuOpen) return undefined

    const armGuardForFocusHandoff = (event) => {
      if (filterMenuTriggerCloseRequested) return
      if (!isFilterMenuPanelFocusHandoffTarget(event.relatedTarget)) return

      rememberFilterMenuPageScrollPositions()
      armTemporaryFilterMenuDismissGuard()
    }

    const armGuardForMenuFocus = (event) => {
      if (filterMenuTriggerCloseRequested) return
      if (!isFilterMenuPanelFocusHandoffTarget(event.target)) return

      armTemporaryFilterMenuDismissGuard()
      rememberFilterMenuPageScrollPositions()
      stabilizeCheckboxFocusWithoutPageScroll(event.target)
      restorePageScrollAfterCheckboxFocus()
    }

    const trackProtectedInteraction = (event) => {
      if (isFilterMenuTriggerPointerEvent(event)) {
        noteFilterMenuTriggerPress()
        prepareFilterMenuTriggerClose()
        ignoreDismissRef.current = false
        return
      }

      if (isFilterMenuPanelCheckboxPointerEvent(event)) {
        rememberFilterMenuPageScrollPositions()
        armTemporaryFilterMenuDismissGuard()
      }

      if (isFilterMenuDismissGuardTarget(event)) {
        ignoreDismissRef.current = true
        return
      }

      // Click can fire after pointerdown but before blur-close; keep the guard during
      // the CUI hidden-checkbox focus handoff window.
      if (isWithinPanelCheckboxInteractionWindow()) {
        return
      }

      ignoreDismissRef.current = false
    }

    const clearDismissGuard = () => {
      ignoreDismissRef.current = false
      suppressCloseForPanelCheckboxUntil = 0
    }

    document.addEventListener('focusout', armGuardForFocusHandoff, true)
    document.addEventListener('focusin', armGuardForMenuFocus, true)
    document.addEventListener('pointerdown', trackProtectedInteraction, true)
    document.addEventListener('mousedown', trackProtectedInteraction, true)
    document.addEventListener('click', trackProtectedInteraction, true)

    return () => {
      clearDismissGuard()
      document.removeEventListener('focusout', armGuardForFocusHandoff, true)
      document.removeEventListener('focusin', armGuardForMenuFocus, true)
      document.removeEventListener('pointerdown', trackProtectedInteraction, true)
      document.removeEventListener('mousedown', trackProtectedInteraction, true)
      document.removeEventListener('click', trackProtectedInteraction, true)
    }
  }, [isFilterMenuOpen])

  return ignoreDismissRef
}
